import { handleResponseErrors } from '@restDataAccess/handleResponseErrors';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import type { ApiNameType } from '@/constants';

import { authorizationHeader } from '../authorizationHeader.ts';
import { useClearCacheCallback } from './cacheHooks.ts';
import { assertApiBaseForFetch } from './getApiUrl/getApiUrl.functions.ts';
import { useGetApiUrl } from './useGetApiUrl.ts';

type postProps<T> = {
  baseApiRoute: ApiNameType;
  cacheKeys: string[];
  headers?: HeadersInit;
  identifier?: string;
  returnJsonResult?: boolean;
  route: string;
  /** When set, builds the request path from mutation params (e.g. `{inventoryId}` segments). */
  resolveRoute?: (params: Partial<T>) => string;
  /** POST `{}` instead of serializing mutation params (path-param-only commands). */
  sendEmptyBody?: boolean;
};

export const useAuthApiPost = <T, V = void>({
  baseApiRoute,
  cacheKeys,
  headers,
  identifier,
  returnJsonResult = false,
  route,
  resolveRoute,
  sendEmptyBody = false,
}: postProps<T>) => {
  const auth = useAuth();
  const api = useGetApiUrl(baseApiRoute);
  const postData = async (device: Partial<T>): Promise<V> => {
    try {
      const base = assertApiBaseForFetch(api);
      const resolvedRoute = resolveRoute ? resolveRoute(device) : route;
      const response = await fetch(`${base}${resolvedRoute}`, {
        method: 'POST',
        body: sendEmptyBody ? JSON.stringify({}) : JSON.stringify(device),
        headers: {
          ...authorizationHeader(auth.user),
          'Content-Type': 'application/json',
          ...headers,
        },
      });

      return await handleResponseErrors<V>(response, identifier, returnJsonResult);
    } catch (error) {
      console.error(`Failed to add device ${identifier}`, error);
      throw error;
    }
  };
  const clearCacheCallback = useClearCacheCallback(cacheKeys);

  return useMutation({
    mutationFn: postData,
    onSuccess: () => clearCacheCallback(),
    onError: () => clearCacheCallback(),
  });
};
